import { SortingOrder } from "@shared/types";
import { get } from "lodash";

export const sortByStringProperty = (
  a: any,
  b: any,
  path?: string | string[], //use array for fallback paths
  sortOrder?: SortingOrder
) => {
  if (!sortOrder) sortOrder = SortingOrder.ASCEND;
  const aValue = path ? get(a, path, "") : a;
  const bValue = path ? get(b, path, "") : b;
  let sortResult = 0;
  if (aValue) {
    sortResult = bValue ? aValue.localeCompare(bValue) : -1;
  } else if (bValue) {
    sortResult = aValue ? bValue.localeCompare(aValue) : 1;
  }
  return sortOrder * sortResult;
};

export const sortByNumberProperty = (
  a: any,
  b: any,
  path?: string | string[], //use array for fallback paths
  sortOrder?: SortingOrder,
  abs?: boolean
) => {
  if (!sortOrder) sortOrder = SortingOrder.ASCEND;
  let aValue;
  let bValue;
  let paths = Array.isArray(path) ? path : [path];
  for (let p of paths) {
    aValue = aValue || (p ? get(a, p) : a);
    bValue = bValue || (p ? get(b, p) : b);
  }
  const isNumber = (a: any) => typeof a === "number" && !isNaN(a);
  const aIsNumber = isNumber(aValue);
  const bIsNumber = isNumber(bValue);

  if (!aIsNumber && !bIsNumber) return 0;
  if (!bIsNumber) return -1;
  if (!aIsNumber) return 1;
  if (abs) {
    aValue = Math.abs(aValue);
    bValue = Math.abs(bValue);
  }
  return sortOrder * (aValue - bValue);
};

export const flipKeyToValuesSetObject = (obj: Record<string, Set<string>>) =>
  Object.entries(obj).reduce((acc, [objKey, objValue]) => {
    objValue.forEach((value) => {
      if (!(value in acc)) {
        acc[value] = new Set([objKey]);
      } else {
        acc[value].add(objKey);
      }
    });
    return acc;
  }, {} as Record<string, Set<string>>);

export const getSortedDataBySortSelect = <
  Data,
  SortType extends string | number
>(
  data: Data[],
  sortBy: SortType,
  sortDirection: SortingOrder,
  sortToPath: {
    [sortKey in SortType]?:
      | string
      | string[]
      | ((a: Data, b: Data, sortDirection: SortingOrder) => number);
  },
  stringSorts: SortType[],
  absSorts?: SortType[],
  commonFallbackSort?: (a: Data, b: Data) => number,
  sortToFallbackSort?: {
    [sortKey in SortType]?: (a: Data, b: Data) => number;
  }
): Data[] => {
  const sortPath = sortToPath[sortBy];
  return data?.slice().sort((a, b) => {
    let sortResult;
    if (typeof sortPath === "function") {
      sortResult = sortPath(a, b, sortDirection);
    } else if (stringSorts.includes(sortBy)) {
      sortResult = sortByStringProperty(a, b, sortPath, sortDirection);
    } else {
      sortResult = sortByNumberProperty(
        a,
        b,
        sortPath,
        sortDirection,
        absSorts?.includes(sortBy)
      );
    }

    if (!sortResult && (commonFallbackSort || sortToFallbackSort)) {
      const fallbackSort = sortToFallbackSort?.[sortBy] ?? commonFallbackSort;
      sortResult = fallbackSort?.(a, b) ?? 0;
    }
    return sortResult;
  });
};
